"use client";
import React, { useEffect, useState, useRef, useMemo } from "react";

import type SwiperCore from "swiper";

import { useIsInViewport } from "~/hooks/use-is-in-viewport";

import { DESKTOP_SLICES_PER_VIEW } from "../use-swiper/constants";

interface ReturnType {
  carouselRef: React.RefObject<HTMLElement | null>;
  swiperSlices: (
    | React.ReactElement
    | number
    | string
    | Iterable<React.ReactNode>
    | React.ReactPortal
  )[];
}

export const useLazySwiper = (
  swiper: SwiperCore | undefined,
  children: React.ReactNode,
  handleSliceChange: (swiper: SwiperCore) => void,
  isRightLayout: boolean,
  isNewInCarousel?: boolean,
): ReturnType => {
  const carouselRef = useRef(null);
  const isInViewport = useIsInViewport(carouselRef);
  const childrenInArray = useMemo(() => {
    const childrenArr = React.Children.toArray(children);
    if (isRightLayout || isNewInCarousel) {
      return childrenArr;
    } else {
      const childrenArrReversed = [...childrenArr].reverse();
      return childrenArrReversed;
    }
  }, [children, isRightLayout, isNewInCarousel]);
  const [swiperSlices, setSwiperSlices] = useState(
    childrenInArray.slice(0, DESKTOP_SLICES_PER_VIEW),
  );

  useEffect(() => {
    if (isInViewport && swiperSlices.length !== childrenInArray.length) {
      setSwiperSlices(childrenInArray);
    }
  }, [isInViewport, childrenInArray, swiperSlices.length]);

  useEffect(() => {
    if (swiper) {
      swiper.update();
      handleSliceChange(swiper);
      if (!isRightLayout && !isNewInCarousel) {
        swiper.slideTo(childrenInArray.length, 0, false);
      }
      if (isNewInCarousel) {
        swiper.slideTo(0, 0, false);
      }
    }
  }, [
    swiperSlices,
    childrenInArray.length,
    handleSliceChange,
    isRightLayout,
    swiper,
    isNewInCarousel,
  ]);

  return {
    carouselRef,
    swiperSlices: swiperSlices as ReturnType["swiperSlices"],
  };
};
